<template>
    <div id="RxTransfer-queue-container">
        <card title="Rx Transfer Queue">

            <b-row>
                <b-col md="3">
                    <b-form-group label="Patient Name">
                        <b-form-input v-model="filters.patientName" placeholder="Search patient..." />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Status">
                        <EnumSelector v-model="filters.transferStatus" :enum-codes="transferStatus" :elements-to-discard="[transferStatus.Cancelled, transferStatus.Completed]" clearable />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Direction">
                        <b-form-select v-model="filters.inbound" :options="directionOptions" />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <DateRangeFilter v-model="filters.dateRange" label="Date Range" />
                </b-col>
            </b-row>

            <b-table :items="filteredItems" :fields="fields"
                fixed @row-clicked="handleRowClick" tbody-class="cursor-pointer" show-empty
                empty-text="No transfers found." ref="transferTable" no-provider-sorting
                sort-by="timeStamp" :sort-desc="false">
                <template #cell(patientName)="{value}">
                    <template v-if="value">{{ value }}</template>
                    <em v-else>Patient Not Found - See Request</em>
                </template>
                <template #cell(transferStatusDate)="data">
                    <span v-b-tooltip.hover :title='`Updated by ${data.item.transferStatusSetBy}`'>
                        {{ formatLastActivity(data.value ?? data.item.timeStamp) }}
                    </span>
                </template>
                <template #cell(electronic)="data">
                    <b-badge v-if="data.item.transferMessageId" variant="primary" block>
                        <i title="Electronic Transmission" class="fa fa-bolt"></i>
                        Transfer
                    </b-badge>
                    <b-badge v-if="data.item.requestMessageId" variant="info" block>
                        <i title="Electronic Transmission" class="fa fa-bolt"></i>
                        Request
                    </b-badge>
                </template>
                <template #cell(actions)="data">
                    <b-button v-if="data.item.inbound && data.item.externalPharmacyId" variant="primary" size="sm"
                        @click.prevent="faxTransfer(data.item)">
                        <i class="fa fa-print" />
                        Fax...
                    </b-button>
                </template>
            </b-table>
        </card>

        <b-modal id="transfer-modal" :title="modalTitle" :size="activeIsOutboundTransferRequest ? 'xl' : 'lg'" no-fade
            scrollable :modal-class="{ 'select-transfer': activeIsOutboundTransferRequest }"
            @hide="activeTransfer = null">

            <div>
                <template v-if="activeTransfer?.requestMessageId || activeTransfer?.transferMessageId">
                    <div :id="pdfContainerID" class="no-image">
                        <h3 class="text-center">File not found</h3>
                    </div>
                </template>
            </div>
            <div v-if="activeIsOutboundTransferRequest">
                <SearchComponent :id="activePatient && +activePatient.id" v-model="activePatient" :disabled="false"
                    label="Patient" search-u-r-l="/Patient" required />

                <div>{{ activeTransferAll ? "Prescriptions" : "Prescription" }} to Transfer:</div>
                <PrescriptionsListComponent :prescriptions="activeTransferScripts" :showVoidBox="false"
                    :fieldsKeysToRemove="['patientPay']" :singleSelect="!activeTransferAll"
                    @selection-changed="selectedScripts = $event" />
            </div>

            <template #modal-footer="{ close }">
                <div class="d-flex justify-content-between w-100">
                    <div>
                        <b-button @click="close">Close</b-button>
                    </div>
                    <div class="d-flex" style="gap: 7px;">
                        <template v-if="activeTransfer?.requestMessageId">
                            <b-button v-if="activeTransfer?.inbound" @click="retractActiveTransfer"
                                variant="warning">Retract</b-button>
                            <template v-if="!activeTransfer?.inbound">
                                <b-button @click="startDenyTransfer" variant="danger">Deny</b-button>
                                <b-button @click="transferScripts" variant="success"
                                    :disabled="!selectedScripts.length || loadingTransferScripts">
                                    Transfer
                                </b-button>
                            </template>
                        </template>
                        <template v-if="activeTransfer?.transferMessageId">
                            <b-button v-if="activeTransfer?.inbound" @click="startDenyTransfer"
                                variant="danger">Deny</b-button>
                            <b-button v-if="activeTransfer?.inbound" @click="confirmTransfer"
                                variant="success">Confirm</b-button>
                        </template>
                    </div>
                </div>
            </template>
        </b-modal>

        <b-modal v-model="showDenyModal" id="deny-modal" title="Deny Rx Transfer" no-fade @ok="denyTransfer">
            <b-overlay :show="loadDenySend">
                <b-form-group label="Reason Code">
                    <b-select :options="denyReasonCodes" v-model="denyReasonCode" />
                </b-form-group>

                <b-form-group label="Reason Details">
                    <b-textarea v-model="denyReason" />
                </b-form-group>
            </b-overlay>
        </b-modal>

        <b-modal v-model="showFaxTransfer" title="Fax Transfer Request" no-fade ok-title="Fax Transfer Request"
            :ok-disabled="!activeTransfer?.externalPharmacyFax" @ok="sendFax">
            <div class="d-flex flex-column" v-if="activeTransfer">
                <strong>Faxing To:</strong>
                <span>{{ activeTransfer?.externalPharmacyName }}</span>
                <span>{{ activeTransfer?.externalPharmacyFax }}</span>
                <strong class="text-danger" v-if="!activeTransfer?.externalPharmacyFax">No Fax Number on
                    Pharmacy</strong>
                <div class="p-1"></div>
                <strong>Transfer Status:</strong>
                <span>{{ transferStatus[activeTransfer.transferStatus] }}</span>
                <span>{{ activeTransfer?.transferStatusReason }}</span>
                <span>{{ activeTransferTransferStatusDateFormatted }}</span>
                <span>{{ activeTransfer?.transferStatusSetBy }}</span>
            </div>
        </b-modal>
    </div>
</template>

<script lang="ts">
import Vue from 'vue'
import { Component, Mixins, Watch, Ref } from 'vue-property-decorator';
import Axios from 'axios';
import moment from 'moment';
import { RxTransfer, transferStatus } from '@/models/RxTransfer';
import { BvModalEvent, BvTableFieldArray, BvTableCtxObject, BTable } from "bootstrap-vue";
import { Row } from "element-ui";
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import PDFObject from 'pdfobject';
import SearchComponent from '@/components/SearchComponent.vue';
import EnumSelector from '@/components/EnumSelector.vue';
import DateRangeFilter from '@/components/DateRangeFilter.vue';
import { Patient } from '@/models/Patient';
import { Prescription } from '@/models/Prescription';
import PrescriptionsListComponent from '@/components/Prescription/PrescriptionsListComponent.vue';
import {Note} from "@/models/Note";
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';
import AutoModeService from '@/services/AutoModeService';

@Component({
    name: "RxTransferQueuePage",
    computed: {
        Row() {
            return Row
        }
    },
    components: {SearchComponent, PrescriptionsListComponent, EnumSelector, DateRangeFilter},
})

export default class RxTransferQueuePage extends Mixins(AutoModeHotkeyMixin) {
    protected unfinishedTransfers: Array<RxTransfer> = [];

    protected activeTransfer: RxTransfer | null = null;
    protected activePatient: Patient | null = new Patient();
    protected activeTransferScripts: Prescription[] = [];
    protected activeTransferAll = false;
    protected selectedScripts: Prescription[] = [];
    protected showDenyModal = false;
    protected showFaxTransfer = false;

    protected denyReasonCode: string | null = null;
    protected denyReason = "";

    protected loadDenySend = false;

    protected transferStatus = transferStatus;

    protected filters = {
        patientName: '',
        transferStatus: null,
        inbound: '',
        dateRange: {startDate: '', endDate: ''}
    };

    @Ref('transferTable') transferTable!: BTable;

    created() {
        this.loadUnfinishedRxTransfers();

        const transferId = this.$route.params.transferId;
        if (transferId) {
            this.loadSingleItem(parseInt(transferId));
        }
    }

    @Watch('$route')
    async onRouteChange(newRoute: any, oldRoute: any) {
        if (newRoute.name !== oldRoute.name) {
            return;
        }

        const oldTransferId = oldRoute.params.transferId;
        const newTransferId = newRoute.params.transferId;

        if (oldTransferId !== newTransferId && newTransferId) {
            console.log('Route transferId parameter changed, loading new transfer item');
            await this.loadUnfinishedRxTransfers();
            await this.loadSingleItem(parseInt(newTransferId));
        }
    }

    async loadSingleItem(id: number) {
        try {
            await this.loadUnfinishedRxTransfers();

            const transfer = this.unfinishedTransfers.find(t => t.id === id);

            if (transfer) {
                this.handleRowClick(transfer);
                return;
            }

            const response = await Axios.get<RxTransfer>(`/RxTransfer/${id}`);
            const loadedTransfer = response.data;

            if (loadedTransfer && !this.unfinishedTransfers.find(t => t.id === loadedTransfer.id)) {
                this.unfinishedTransfers.unshift(loadedTransfer);
            }

            if (loadedTransfer) {
                this.handleRowClick(loadedTransfer);
            }
        } catch (error) {
            console.error('Error loading single transfer:', error);
            this.$notification(NotificationOptions.error('Failed to load transfer'));
        }
    }

    get activeTransferTransferStatusDateFormatted() {
        if (!this.activeTransfer?.transferStatusDate) return undefined;
        return moment.utc(this.activeTransfer?.transferStatusDate).local().format("LLL");
    }


    get hasElectronicTransfers() {
        return this.unfinishedTransfers.some(t => t.transferMessageId || t.requestMessageId);
    }

    get fields(): BvTableFieldArray {
        return [
            { key: "electronic", label: "", class: "electronic", hidden: !this.hasElectronicTransfers },
            { key: "timeStamp", label: "Entered", formatter: this.convertDate, sortable: true },
            { key: "inbound", label: "In/Out", formatter: (value: any) => value ? "Inbound" : "Outbound", sortable: true },
            { key: "patientName", formatter: (value: any) => value === ", " ? "" : value, sortable: true },
            { key: "patientDOB", label: "DOB", formatter: this.convertDate, sortable: true },
            { key: "patientState", sortable: true },
            { key: "transferStatus", label: "Status", formatter: this.getEnum, sortable: true },
            { key: "drugName", sortable: true },
            { key: "programName", sortable: true },
            { key: "transferStatusDate",label: "Last Activity", sortable: true, sortByFormatted: false },
            { key: "rxTransferReasonName", label: "Reason", sortable: true },
            { key: "notes",label: "Notes",formatter: (notes: Note[] | null) => notes?.at(-1)?.body ?? "", sortable: true },
            { key: "actions", label: "", class: "actions" }
        ].filter(f => f.hidden !== true);
    }

    formatLastActivity(date?: string | Date) {
        if (!date) return "";
        const momentDate = moment.utc(date);
        if (!momentDate.isValid()) return "";
        return momentDate.local().fromNow();
    }

    async loadUnfinishedRxTransfers() {
        try {
            const response = await Axios.get<RxTransfer[]>(`/RxTransfer/GetUnfinished`);
            this.unfinishedTransfers = response.data;
        } catch (err) {
            console.warn(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async handleAutoModeCompletion() {
        await this.handleAutoModeNextItem();
    }

    convertDate(date?: string) {
        if (!date) return "";
        return moment.utc(date).format("L");
    }

    getEnum(status: number) {
        return transferStatus[status];
    }

    handleRowClick(row: RxTransfer) {
        if (row.requestMessageId || row.transferMessageId) {
            this.activeTransfer = row;
            this.activePatient = new Patient();
            this.$bvModal.show("transfer-modal");
            this.getTransferMessageData();
            this.getTransferDetails();
            return;
        }
        this.$router.push({ name: 'PatientDashboard', params: { id: row.patientId.toString() } });
    }

    async retractActiveTransfer(e: any) {
        if (!this.activeTransfer) return;

        e.preventDefault();

        try {
            await this.retractTransfer(this.activeTransfer);
            this.$bvModal.hide("transfer-modal");
            await this.loadUnfinishedRxTransfers();
            this.$notification(NotificationOptions.success("Transfer successfully retracted"));

            await this.handleAutoModeCompletion();
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async retractTransfer(transfer: RxTransfer) {
        try {
            await this.$http.post(`/RxTransfer/retract-request/${transfer.requestMessageId}`);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get pdfContainerID() {
        return `pdf-${this.activeTransfer?.id}`;
    }

    async getTransferMessageData() {
        try {
            const response = await this.$http.get(`RxTransfer/${this.activeTransfer?.id}/MessageData`, { responseType: "blob", });

            if (response.data) {
                const objectUrl = URL.createObjectURL(response.data);
                const options = {
                    pdfOpenParams: {
                        view: 'FitW',
                        pagemode: 'thumbs',
                        toolbar: 1,
                        scrollbar: 0,
                    }
                };
                PDFObject.embed(objectUrl, `#${this.pdfContainerID}`, options);
            }
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async getTransferDetails() {
        try {
            const response = await this.$http.get(`RxTransfer/${this.activeTransfer?.id}`);

            if (response.data.mappings) {
                this.activePatient = Object.assign(new Patient(), response.data.mappings.patientObj);
                this.activeTransferAll = response.data.mappings.transferAll;
            }
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async confirmTransfer() {
        try {
            await this.$http.post(`RxTransfer/${this.activeTransfer?.id}/ConfirmTransfer`);
            this.$notification(NotificationOptions.success("Confirmed transfer and added prescriptions to intake queue."));
            await this.loadUnfinishedRxTransfers();
            this.$bvModal.hide("transfer-modal");

            await this.handleAutoModeCompletion();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get modalTitle() {
        if (this.activeTransfer?.transferMessageId) return "Rx Transfer Details";
        return "Rx Transfer Request Details";
    }

    startDenyTransfer() {
        this.showDenyModal = true;
    }

    async denyTransfer(e: BvModalEvent) {
        try {
            e.preventDefault();

            if (!this.denyReasonCode) {
                this.$notification(NotificationOptions.error("Select a reason code"));
                return;
            }

            this.loadDenySend = true;

            await this.$http.post(`RxTransfer/${this.activeTransfer?.id}/DenyTransfer`, { denialReasonCodes: [this.denyReasonCode], denialReason: this.denyReason });
            this.$notification(NotificationOptions.success("Sent denied message for transfer."));
            await this.loadUnfinishedRxTransfers();

            this.showDenyModal = false;
            this.$bvModal.hide("transfer-modal");

            await this.handleAutoModeCompletion();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loadDenySend = false;
        }
    }

    get denyReasonCodes() {
        return [
            { value: "AA", text: "Patient unknown (AA)" },
            { value: "AP", text: "Will be responded to by other means (phone, fax) (AP)" },
            { value: "BB", text: "No information available (BB)" },
            { value: "CS", text: "Prescription not found (CS)" },
            { value: "CQ", text: "Transfer needs to be discussed (CQ)" }
        ]
    }

    get activeIsOutboundTransferRequest() {
        return this.activeTransfer?.requestMessageId && !this.activeTransfer?.inbound;
    }

    @Watch("activePatient")
    handleActivePatientChange(newValue: Patient, oldValue: Patient) {
        if (newValue.id == oldValue.id) return;
        this.getTransferScriptsForPatient();
    }

    async getTransferScriptsForPatient() {
        try {
            this.activeTransferScripts = [];
            if (!this.activePatient?.id) return;
            const response = await this.$http.get<Prescription[]>(`RxTransfer/prescriptions/${this.activePatient?.id}`);

            this.activeTransferScripts = response.data.map(p => Object.assign(new Prescription(), p));
        }
        catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    protected loadingTransferScripts = false;

    async transferScripts() {
        try {
            this.loadingTransferScripts = true;
            await this.$http.post(`RxTransfer/${this.activeTransfer?.id}/Transfer`, this.selectedScripts);

            this.$notification(NotificationOptions.success("Sent transfer"));

            this.$bvModal.hide("transfer-modal");

            await this.loadUnfinishedRxTransfers();

            await this.handleAutoModeCompletion();
        }
        catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loadingTransferScripts = false;
        }
    }

    async faxTransfer(transfer: RxTransfer) {
        this.activeTransfer = transfer;
        this.showFaxTransfer = true;
    }

    async sendFax() {
        try {
            await this.$http.post("/RxTransfer/SendFaxFromQueue/" + this.activeTransfer!.id);
            this.$notification(NotificationOptions.success("Sent transfer request fax"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get filteredItems() {
        return this.unfinishedTransfers.filter((item: any) => {
            // Transfer status filter
            if (this.filters.transferStatus !== null && this.filters.transferStatus !== '' && this.filters.transferStatus !== undefined) {
                if (item.transferStatus !== parseInt(this.filters.transferStatus)) {
                    return false;
                }
            }

            // Patient name filter
            if (this.filters.patientName?.trim()) {
                const patientName = item.patientName || '';
                if (!patientName.toLowerCase().includes(this.filters.patientName.trim().toLowerCase())) {
                    return false;
                }
            }

            // Direction filter
            if (this.filters.inbound !== '') {
                const isInbound = this.filters.inbound === 'true';
                if (item.inbound !== isInbound) {
                    return false;
                }
            }

            // Date range filter
            if (this.filters.dateRange?.startDate || this.filters.dateRange?.endDate) {
                const itemDate = new Date(item.timeStamp);

                if (this.filters.dateRange.startDate) {
                    const startDate = new Date(this.filters.dateRange.startDate);
                    if (itemDate < startDate) {
                        return false;
                    }
                }

                if (this.filters.dateRange.endDate) {
                    const endDate = new Date(this.filters.dateRange.endDate);
                    endDate.setHours(23, 59, 59, 999); // End of day
                    if (itemDate > endDate) {
                        return false;
                    }
                }
            }

            return true;
        });
    }

    get directionOptions() {
        return [
            {value: '', text: 'All Directions'},
            {value: 'true', text: 'Inbound'},
            {value: 'false', text: 'Outbound'}
        ];
    }
}

</script>

<style scoped>
::v-deep(.electronic) {
    width: 90px;
}

::v-deep(.no-image) {
    height: calc(100vh - 230px);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
}

::v-deep(.select-transfer .modal-dialog) {
    max-width: 95vw;
}

::v-deep(.actions) {
    width: 100px;
}

@media screen and (min-width: 1200px) {
    ::v-deep(.select-transfer .modal-body) {
        display: grid;
        grid-template-columns: 35vw auto;
        gap: 10px;
    }
}
</style>
